import { useState } from 'react'
import { Banner, Button, CollapsibleCard, SectionLabel } from '../../../design/components'
import { useSceneSnapshot } from '../queries'
import type { SceneCharactersSection } from '../types'
import type { SceneEditing } from '../useSceneEditing'
import styles from './play.module.css'

/**
 * The character-state snapshot — the formatted rundown of everyone in the scene that
 * comic-page generation reads.
 *
 * **Fetched only when opened.** It is its own endpoint precisely because it is a
 * multi-kilobyte blob most scene opens never show, so loading it with the sheet would
 * throw away the reason the API split it.
 *
 * Staleness is the server's call, not this card's: the snapshot ships the cast size it
 * would cover if regenerated now, and comparing that with the cast in play is the only
 * derivation here — a count against a count, not a reconstruction of the text.
 */
export function SnapshotCard({
  sceneId,
  hasSnapshot,
  characters,
  editing,
}: {
  sceneId: number
  hasSnapshot: boolean
  characters: SceneCharactersSection | undefined
  editing: SceneEditing
}) {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<string | null>(null)

  const query = useSceneSnapshot(sceneId, open)
  const snapshot = query.data?.sections.snapshot

  const castSize = (characters?.all ?? []).length
  const stale =
    snapshot !== undefined && snapshot.text !== null && snapshot.characterCount !== castSize

  return (
    <CollapsibleCard variant="bio" label="Snapshot" summary={hasSnapshot ? 'recorded' : 'none yet'}>
      {/* A `<details>` renders its children whether open or not, so opening the card
          cannot be the trigger — it would fetch the blob for anyone who scrolled past.
          An explicit button is both simpler and honest about the cost. */}
      <div>
        {!open && (
          <div className={styles.actions}>
            <Button variant="secondary" onClick={() => setOpen(true)}>
              {hasSnapshot ? 'Load the snapshot' : 'Check for a snapshot'}
            </Button>
          </div>
        )}

        {open && query.isPending && <p className={styles.note}>Loading…</p>}

        {open && query.isError && (
          <Banner tone="error">Could not load this scene&rsquo;s snapshot.</Banner>
        )}

        {open && query.isSuccess && (
          <>
            {stale && (
              <Banner tone="warn">
                This snapshot covers {snapshot?.characterCount} of the {castSize} now in the scene.
                Regenerate it before generating pages.
              </Banner>
            )}

            {snapshot?.text === null || snapshot?.text === undefined ? (
              <p className={styles.empty}>No snapshot recorded.</p>
            ) : draft === null ? (
              <p className={styles.rowText}>{snapshot.text}</p>
            ) : (
              <label className={styles.field}>
                <SectionLabel tone="muted" micro>
                  Snapshot text
                </SectionLabel>
                <textarea
                  className={styles.textarea}
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                />
              </label>
            )}

            {editing.canEdit && (
              <div className={styles.actions}>
                {draft === null ? (
                  <>
                    <Button
                      variant="secondary"
                      disabled={editing.regenerateSnapshot.isPending}
                      onClick={() => editing.regenerateSnapshot.mutate(undefined)}
                    >
                      {editing.regenerateSnapshot.isPending ? 'Rebuilding…' : 'Regenerate'}
                    </Button>
                    {snapshot?.text !== null && snapshot?.text !== undefined && (
                      <>
                        <Button variant="secondary" onClick={() => setDraft(snapshot.text ?? '')}>
                          Edit
                        </Button>
                        <Button
                          variant="danger"
                          disabled={editing.clearSnapshot.isPending}
                          onClick={() => editing.clearSnapshot.mutate(undefined)}
                        >
                          Clear
                        </Button>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <Button variant="secondary" onClick={() => setDraft(null)}>
                      Cancel
                    </Button>
                    <Button
                      disabled={editing.setSnapshot.isPending || draft.trim() === ''}
                      onClick={() =>
                        editing.setSnapshot.mutate(
                          { text: draft },
                          { onSuccess: () => setDraft(null) },
                        )
                      }
                    >
                      {editing.setSnapshot.isPending ? 'Saving…' : 'Save'}
                    </Button>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </CollapsibleCard>
  )
}
